import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import BasicECharts from 'components/common/BasicEChart';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useAppContext } from 'providers/AppProvider';
import React, { useMemo, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const LATE_FIELDS = [
  { key: 'billed_late', name: 'Billed', color: '#ff8566' },
  { key: 'robot_late', name: 'Robot', color: '#1000ff' },
  { key: 'manual_late', name: 'Manual', color: '#8b0a8f' },
  { key: 'pv2_late', name: 'PV2', color: '#008a0a' },
  { key: 'manifest_late', name: 'Manifest', color: '#9a4f12' }
];

const toNumber = value => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const getFacilityName = facilityId => {
  try {
    const facilitiesData = JSON.parse(
      localStorage.getItem('facilitiesData') || '[]'
    );
    return facilitiesData.find(
      facility => Number(facility.value) === Number(facilityId)
    )?.key;
  } catch {
    return '';
  }
};

const getOptions = (getThemeColor, chartData, totalLate, hasLateData) => ({
  color: chartData.map(item => item.chartColor),
  tooltip: {
    show: hasLateData,
    trigger: 'item',
    padding: [8, 12],
    transitionDuration: 0,
    backgroundColor: getThemeColor('gray-100'),
    borderColor: getThemeColor('gray-300'),
    borderWidth: 1,
    textStyle: { color: getThemeColor('gray-1100') },
    formatter: params =>
      `<strong>${params.name}</strong><br/>${params.value} cycles (${params.percent}%)`
  },
  series: [
    {
      name: 'Late Cycles',
      type: 'pie',
      radius: ['48%', '78%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: true,
      minAngle: 4,
      padAngle: 2,
      itemStyle: {
        borderRadius: 8,
        borderWidth: 3,
        borderColor: getThemeColor('gray-100')
      },
    //   label: {
    //     show: true,
    //     position: 'center',
    //     formatter: `{total|${totalLate}}\n{label|Late Cycles}`,
    //     rich: {
    //       total: {
    //         fontSize: 28,
    //         fontWeight: 700,
    //         color: getThemeColor('gray-1100'),
    //         lineHeight: 34
    //       },
    //       label: {
    //         fontSize: 12,
    //         fontWeight: 600,
    //         color: getThemeColor('gray-600'),
    //         lineHeight: 18
    //       }
    //     }
    //   },
    //   labelLine: { show: false },
      emphasis: {
        scale: true,
        scaleSize: 8,
        itemStyle: {
          shadowBlur: 18,
          shadowColor: 'rgba(0, 0, 0, 0.18)'
        }
      },
      data: chartData
    }
  ]
});

const LateMetric = ({ item, total }) => {
  const percentage =
    total > 0 ? ((item.value * 100) / total).toFixed(1) : '0.0';

  return (
    <div className="py-2 px-3 rounded-2 bg-100">
      <Flex alignItems="center" justifyContent="between" className="gap-2">
        <div className="min-w-0">
          <div className="fw-semibold text-900 text-truncate">
            <FontAwesomeIcon
              icon="circle"
              className="me-2"
              style={{ color: item.color }}
            />
            {item.name}
          </div>
          <div className="fs-11 text-600">{percentage}% of late cycles</div>
        </div>
        <div className="fw-bold" style={{ color: item.color }}>
          {item.value}
        </div>
      </Flex>
    </div>
  );
};

LateMetric.propTypes = {
  item: PropTypes.shape({
    color: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired
  }).isRequired,
  total: PropTypes.number.isRequired
};

const CycleSummary = ({ cycleSummaryDetails = {} }) => {
  const { getThemeColor } = useAppContext();
  const [selectedFacility, setSelectedFacility] = useState({
    value: 'ALL',
    label: 'All Facilities'
  });

  const facilityRows = Array.isArray(
    cycleSummaryDetails?.cycleSummaryByFacility
  )
    ? cycleSummaryDetails.cycleSummaryByFacility
    : [];

  const facilityOptions = useMemo(
    () => [
      { value: 'ALL', label: 'All Facilities' },
      ...facilityRows.map(facility => ({
        value: String(facility.facility_id),
        label:
          getFacilityName(facility.facility_id) ||
          `Facility ${facility.facility_id}`
      }))
    ],
    [facilityRows]
  );

  const selectedSummary = useMemo(() => {
    if (selectedFacility.value !== 'ALL') {
      return (
        facilityRows.find(
          facility =>
            String(facility.facility_id) === String(selectedFacility.value)
        ) || {}
      );
    }

    if (cycleSummaryDetails?.cycleSummary) {
      return cycleSummaryDetails.cycleSummary;
    }

    return facilityRows.reduce((acc, facility) => {
      LATE_FIELDS.forEach(({ key }) => {
        acc[key] = toNumber(acc[key]) + toNumber(facility[key]);
      });
      acc.total_cycles =
        toNumber(acc.total_cycles) + toNumber(facility.total_cycles);
      return acc;
    }, {});
  }, [cycleSummaryDetails, facilityRows, selectedFacility]);

  const lateItems = useMemo(
    () =>
      LATE_FIELDS.map(field => ({
        ...field,
        value: toNumber(selectedSummary[field.key]),
        chartColor: field.color
      })),
    [selectedSummary]
  );

  const totalLate = lateItems.reduce((total, item) => total + item.value, 0);
  const hasLateData = totalLate > 0;
  const chartData = hasLateData
    ? lateItems
    : [
        {
          name: 'No Late Cycles',
          value: 1,
          color: 'secondary',
          chartColor: getThemeColor('gray-300')
        }
      ];

  return (
    <Card className="h-100 overflow-hidden">
      <FalconCardHeader
        title="Cycle Late Summary"
        light={false}
        titleTag="h6"
        className="pb-0"
      />
      <Card.Body className="pt-3">
        <Row className="g-3 align-items-stretch">
          <Col xs={12} lg={5}>
            {/* <ThemedSelect
              value={selectedFacility}
              onChange={setSelectedFacility}
              options={facilityOptions}
              placeholder="Select Facility"
            /> */}

            {/* <div
              className="mt-3 p-3 rounded-2"
              style={{
                background:
                  'linear-gradient(135deg, rgba(44,123,229,.12), rgba(230,55,87,.10))'
              }}
            >
              <div className="text-600 fs-11 text-uppercase fw-semibold">
                Late Coverage
              </div>
              <Flex alignItems="end" justifyContent="between" className="mt-1">
                <div className="display-6 fw-bold lh-1 text-900">
                  {lateRate}%
                </div>
                <div className="text-end">
                  <div className="fw-bold text-900">{totalCycles}</div>
                  <div className="fs-11 text-600">Total cycles</div>
                </div>
              </Flex>
            </div> */}

            <div className="mt-3 d-grid gap-2">
              {lateItems.map(item => (
                <LateMetric key={item.key} item={item} total={totalLate} />
              ))}
            </div>
          </Col>

          <Col xs={12} lg={7}>
            <div className="h-100 d-flex align-items-center justify-content-center">
              <BasicECharts
                echarts={echarts}
                options={getOptions(
                  getThemeColor,
                  chartData,
                  totalLate,
                  hasLateData
                )}
                style={{ width: '100%', minHeight: '20rem' }}
              />
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

CycleSummary.propTypes = {
  cycleSummaryDetails: PropTypes.shape({
    cycleSummary: PropTypes.object,
    cycleSummaryByFacility: PropTypes.arrayOf(PropTypes.object)
  })
};

export default CycleSummary;
